// src/services/azureResearchProjectsService.js
// Azure Table Storage Service für Research-Projekt-Definitionen.
//
// Ein Research-Projekt bündelt eigene Q&A-/Zitate-Einträge (Tabelle researchQnA,
// partitioniert je Projekt — siehe azureResearchService.js) plus optionale,
// extern befüllte Quellen (SourceTable) und Blob-Dateien (FilesPrefix im
// Container research-files).
//
// Tabelle: researchProjects
//  - PartitionKey: "research" (fix)
//  - RowKey:       rp_<uuid>  (Projekt-ID; die migrierte Sakralbauten-Recherche
//                  hat die fixe ID rp_sakralbauten021 — siehe Transfer-Script
//                  scripts/migrate-research-projects.mjs)
//
// Sicherheits-Hinweis: Der SAS-Token ist – wie in allen bestehenden Services –
// hartcodiert und landet im Frontend-Bundle. Mittelfristig rotieren und über
// einen Backend-Proxy/Env-Variable beziehen.

import { ensureTableExists } from "./azureTableUtils";
import { SHARED_SOURCES_TABLE } from "./azureResearchService";

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "researchProjects",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const PARTITION = "research";

const baseUrl = () =>
  `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}`;

const entityUrl = (id) =>
  `${baseUrl()}(PartitionKey='${PARTITION}',RowKey='${encodeURIComponent(id)}')?${AZURE_CONFIG.sasToken}`;

const cleanValue = (v) => (v === null || v === undefined ? "" : String(v));

// Standard-Beschriftung des dritten Reiters; pro Projekt überschreibbar
// (CaseTabLabel, z.B. "Kirchen" beim Sakralbauten-Projekt).
export const DEFAULT_CASE_TAB_LABEL = "Case Studies";

const mapProject = (item) => ({
  id: item.RowKey,
  name: item.Name || "",
  description: item.Description || "",
  // Extern befüllte Quellen-Tabelle (z.B. Sakralbauten021Research); leer = keine.
  sourceTable: item.SourceTable || "",
  // Ordner-Präfix im Blob-Container research-files (z.B. "rp_xyz/"); leer = Container-Wurzel.
  filesPrefix: item.FilesPrefix || "",
  // Projektspezifische Beschriftung des dritten Reiters; leer = DEFAULT_CASE_TAB_LABEL.
  caseTabLabel: item.CaseTabLabel || "",
  createdBy: item.CreatedBy || "",
  createdAt: item.CreatedAt || "",
  updatedAt: item.UpdatedAt || "",
  deletedAt: item.DeletedAt || "",
});

// Alle (nicht gelöschten) Research-Projekte laden, alphabetisch nach Name.
export const getAllResearchProjects = async () => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    let all = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${baseUrl()}?${AZURE_CONFIG.sasToken}&$filter=${encodeURIComponent(
        `PartitionKey eq '${PARTITION}'`
      )}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      all = all.concat(data.value || []);
      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return all
      .map(mapProject)
      .filter((p) => !p.deletedAt)
      .sort((a, b) => a.name.localeCompare(b.name, "de"));
  } catch (error) {
    console.error("Load Research Projects Error:", error);
    return [];
  }
};

// Neues Research-Projekt anlegen. Eigene Dateien landen in einem projekteigenen
// Blob-Ordner (FilesPrefix = "<id>/"), damit sich Projekte nicht vermischen.
// Quellen: neue Projekte nutzen standardmässig die gemeinsame Tabelle
// researchSources (Partition = Projekt-ID); eine abweichende, projekteigene
// Tabelle kann programmatisch via sourceTable übergeben werden.
export const createResearchProject = async ({
  name,
  description = "",
  sourceTable = SHARED_SOURCES_TABLE,
  createdBy = "",
} = {}) => {
  await ensureTableExists(AZURE_CONFIG.tableName);
  const id = `rp_${crypto.randomUUID()}`;
  const now = new Date().toISOString();

  const entity = {
    PartitionKey: PARTITION,
    RowKey: id,
    Name: cleanValue(name),
    Description: cleanValue(description),
    SourceTable: cleanValue(sourceTable || SHARED_SOURCES_TABLE),
    FilesPrefix: `${id}/`,
    CreatedBy: cleanValue(createdBy),
    CreatedAt: now,
    UpdatedAt: now,
  };

  const response = await fetch(entityUrl(id), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });
  if (!response.ok) {
    const errorText = await response.text();
    console.error("Azure Error Details:", errorText);
    throw new Error(`Azure Save Error: ${response.status} - ${errorText}`);
  }

  return mapProject(entity);
};

// Teil-Update via MERGE (nur übergebene Felder), damit DeletedAt/CreatedBy usw.
// erhalten bleiben.
export const updateResearchProject = async (id, fields = {}) => {
  await ensureTableExists(AZURE_CONFIG.tableName);
  const now = new Date().toISOString();

  const entity = { PartitionKey: PARTITION, RowKey: id, UpdatedAt: now };
  if (fields.name !== undefined) entity.Name = cleanValue(fields.name);
  if (fields.description !== undefined) entity.Description = cleanValue(fields.description);
  if (fields.sourceTable !== undefined) entity.SourceTable = cleanValue(fields.sourceTable);
  if (fields.caseTabLabel !== undefined) entity.CaseTabLabel = cleanValue(fields.caseTabLabel);

  const response = await fetch(entityUrl(id), {
    method: "MERGE",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
      "If-Match": "*",
    },
    body: JSON.stringify(entity),
  });
  if (!response.ok) {
    const errorText = await response.text();
    console.error("Azure Error Details:", errorText);
    throw new Error(`Azure Update Error: ${response.status} - ${errorText}`);
  }
  return { id, ...fields, updatedAt: now };
};

// Soft-Delete: Projekt verschwindet aus der Liste, Einträge/Quellen bleiben
// in den Tabellen erhalten (Wiederherstellung durch Leeren von DeletedAt möglich).
export const softDeleteResearchProject = async (id) => {
  await ensureTableExists(AZURE_CONFIG.tableName);
  const response = await fetch(entityUrl(id), {
    method: "MERGE",
    headers: {
      "Content-Type": "application/json",
      "If-Match": "*",
    },
    body: JSON.stringify({
      PartitionKey: PARTITION,
      RowKey: id,
      DeletedAt: new Date().toISOString(),
    }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Azure Delete Error: ${response.status} - ${errorText}`);
  }
};

// --- Letztgeöffnetes Projekt (localStorage) ----------------------------------
// Beim Klick auf "Research" in der Navigation wird das zuletzt geöffnete
// Projekt automatisch wieder geöffnet (Zurück-Button führt zur Übersicht).

const LAST_OPENED_KEY = "researchLastProjectId";

export const getLastResearchProjectId = () => {
  try {
    return localStorage.getItem(LAST_OPENED_KEY) || "";
  } catch {
    return "";
  }
};

export const setLastResearchProjectId = (id) => {
  try {
    if (id) localStorage.setItem(LAST_OPENED_KEY, id);
    else localStorage.removeItem(LAST_OPENED_KEY);
  } catch {
    // localStorage nicht verfügbar (z.B. Privacy-Modus) — Auto-Open entfällt dann.
  }
};
